import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Container, Row, Col, Modal, Form } from 'react-bootstrap';
import styled from 'styled-components';

// Styled Components
const StyledContainer = styled(Container)`
  background-color: #232830;
  color: #ffffff;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const StyledHeading = styled.h2`
  font-size: 22px;
  font-weight: bold;
  color: #ff7c24;
  text-align: center;
  margin-bottom: 20px;
`;

const QuestionText = styled.h3`
  font-size: 20px;
  font-weight: bold;
  color: #ffffff;
  text-align: center;
  margin-bottom: 20px;
`;

const StyledCard = styled(Card)`
  background-color: ${(props) =>
    props.selected ? (props.isCorrect ? '#2ECC71' : '#FF4C4C') : '#FFFFFF'};
  color: ${(props) => (props.selected ? '#FFFFFF' : '#232830')};
  border-radius: 15px;
  padding: 15px;
  margin-bottom: 10px;
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
  &:hover {
    transform: scale(1.02);
  }
`;

// Styled Button for "Learn with AI"
const AIButton = styled(Button)`
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
  font-size: 18px;
  font-weight: bold;
  border-radius: 12px;
  padding: 12px;
  width: 80%;
  max-width: 300px;
  margin-top: 10px;
  &:hover {
    background-color: #0056b3;
    border-color: #0056b3;
  }
`;

const UpgradeContainer = styled.div`
  padding: 20px;
  background-color: rgba(0, 0, 255, 0.7);
  border-radius: 8px;
  color: #ffffff;
  text-align: left;
  margin-top: 15px;
`;

const UpgradeButton = styled(Button)`
background-color: #ff7c24;
border-color: #ff7c24;
font-size: 18px;
font-weight: bold;
padding: 12px;
width: 100%;
margin-top: 10px;
&:hover {
  background-color: #e66a1a;
  border-color: #e66a1a;
}
`;

const handleUpgradeClick = () => {
    window.open("https://buy.stripe.com/eVa29y1Ig8zsgFycMZ", "_blank", "noopener,noreferrer");
  };

const SendButton = styled(Button)`
  background-color: var(--main-orange);
  border-color: var(--main-orange);
  color: var(--main-white);
  font-size: 16px;
  font-weight: bold;
  border-radius: 8px;
  padding: 10px 20px;
  &:hover {
    background-color: var(--main-black);
    border-color: var(--main-black);
  }
`;


const OnboardingMultipleChoiceQuestion = ({ question, onNext }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userMessage, setUserMessage] = useState('');
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [showUpgradeOverlay, setShowUpgradeOverlay] = useState(false);
  const chatEndRef = useRef(null);

  const aiResponses = {
   "mc-onbaordingquestion-52": [
                      "• Safe-haven currencies become attractive in uncertain times.\n• Investors seek stability amid volatility.\n• Currency trends reflect global risk sentiment.",
                      "• Monitor central bank announcements and economic indicators.\n• Review forex charts for trend confirmation.\n• Compare historical performance during crises.",
                      "• Analyze current market trends.\n• Set stop-loss orders for protection.\n• Consider initiating a long Yen position.",
                      "• The Yen historically appreciates during crises, acting as a reliable safe haven."
                  ],
                  "mtf-onbaordingquestion-22": [
                      "• Risk management tools limit losses and protect capital.\n• They enforce discipline in volatile markets.\n• They provide a framework for safe trading.",
                      "• Monitor risk through automated tools and alerts in live markets.\n• Integrate risk calculators with trading platforms.\n• Adjust positions based on risk tolerance.",
                      "• Consistently use stop-loss orders.\n• Diversify risk management techniques.\n• Regularly review and update risk settings.",
                      "• Effective risk management is essential for long-term trading success and capital preservation."
                  ],
                  "mtf-onbaordingquestion-13": [
                      "• Valuation metrics (P/E, P/B, ROE) assess financial health.\n• They compare a stock’s price relative to its earnings and assets.\n• They help identify over- or undervalued stocks.",
                      "• Compare metrics against industry benchmarks using financial reports.\n• Use analyst data for additional insights.\n• Review historical trends to predict future performance.",
                      "• Analyze quarterly and annual reports.\n• Compare valuation ratios with peers.\n• Use valuation models to guide investment decisions.",
                      "• Proper valuation can uncover hidden investment opportunities often overlooked."
                  ],
                  "mc-onbaordingquestion-49": [
                      "• Algorithms execute trades in milliseconds.\n• Speed captures fleeting opportunities.\n• Automation reduces human error.",
                      "• Use high-frequency trading systems to exploit price inefficiencies.\n• Continuously monitor execution speeds and latency.\n• Adjust algorithms based on market conditions.",
                      "• Set up and optimize your trading algorithms.\n• Monitor system performance closely.\n• Fine-tune parameters for a competitive advantage.",
                      "• A millisecond advantage in algorithmic trading can yield significant profits in high-volume markets."
                  ],
                  "mc-onbaordingquestion-4": [
                      "• Hype-driven surges often lack solid fundamentals.\n• Prices may be overinflated during bubbles.\n• Caution is needed to avoid entering a bubble.",
                      "• Evaluate both social sentiment and fundamental data.\n• Use on-chain metrics for confirmation.\n• Compare with historical hype events for perspective.",
                      "• Wait for fundamentals to catch up.\n• Avoid impulsive trades based on hype.\n• Reassess market conditions before entering.",
                      "• Many crypto bubbles burst after the initial hype, leading to steep corrections."
                  ],
                  "tf-onbaordingquestion-4": [
                      "• Bitcoin halving reduces supply gradually, not instantly.\n• It affects the long-term price trajectory rather than immediate moves.\n• It shifts the supply-demand balance over time.",
                      "• Analyze historical halving data to understand timing.\n• Monitor on-chain activity and miner behavior post-halving.\n• Focus on long-term trends rather than immediate moves.",
                      "• Review past halving events carefully.\n• Adjust your trading strategy to a longer horizon.\n• Focus on long-term price trends.",
                      "• Bitcoin halving events have led to delayed rallies rather than immediate drops."
                  ],
                  "mc-onbaordingquestion-50": [
                      "• Derivative hedging reduces risk exposure in volatile markets.\n• It offsets potential losses using financial instruments.\n• It is an integral part of risk management.",
                      "• Use protective puts or covered calls to hedge positions.\n• Monitor market volatility to adjust hedges.\n• Integrate hedging strategies into portfolio management.",
                      "• Identify high-risk areas in your portfolio.\n• Implement appropriate hedging instruments.\n• Continuously monitor and adjust your hedges.",
                      "• Many institutional investors rely on derivative hedging to safeguard their portfolios."
                  ],
                  "mc-onbaordingquestion-58": [
                      "• A bullish moving average crossover signals upward momentum.\n• It indicates a trend shift in the market.\n• Moving averages smooth out price data for clearer signals.",
                      "• Use moving average crossovers to time entries.\n• Confirm signals with volume and additional indicators.\n• Apply multiple timeframes for robust validation.",
                      "• Enter long positions when the crossover is confirmed.\n• Set protective stop-loss orders to manage risk.\n• Monitor trend strength and adjust accordingly.",
                      "• Historical data shows bullish crossovers often precede sustained uptrends."
                  ],
                  "mtf-onbaordingquestion-2": [
                      "• Price action reflects real-time supply and demand dynamics.\n• Candlestick patterns reveal market sentiment.\n• It provides a direct insight into market behavior.",
                      "• Use chart analysis to identify support and resistance levels.\n• Monitor for breakout or reversal signals.\n• Apply technical analysis to inform trade entries.",
                      "• Observe key price patterns and adjust your trading strategy.\n• Validate signals with complementary technical tools.\n• Incorporate additional market data for confirmation.",
                      "• Many traders consider price action the purest form of market analysis."
                  ],
                  "mc-onbaordingquestion-26": [
                      "• Halving events reduce supply and increase scarcity over time.\n• They impact market psychology and investor sentiment.\n• They are key events in Bitcoin’s economic model.",
                      "• Compare historical halving data to observe delayed price reactions.\n• Monitor market sentiment and miner activity.\n• Use on-chain data for deeper insights.",
                      "• Track the halving event using multiple sources.\n• Adjust positions gradually based on observed trends.\n• Maintain a long-term perspective in your strategy.",
                      "• Bitcoin halving is one of the most closely watched events in crypto, often leading to significant rallies."
                  ],
                  "mtf-onbaordingquestion-21": [
                      "• Emotional biases, such as fear and greed, distort trading decisions.\n• They result in impulsive actions and mistakes.\n• Recognizing these biases is crucial for improvement.",
                      "• Use trading journals and psychological assessments to monitor emotions.\n• Implement mindfulness and discipline techniques.\n• Seek mentorship to overcome biases.",
                      "• Keep a detailed trade journal and reflect on emotional triggers.\n• Adjust strategies based on feedback and analysis.\n• Continuously work to mitigate bias-driven decisions.",
                      "• Many top traders attribute their success to mastering trading psychology."
                  ],
                  "mc-onbaordingquestion-38": [
                      "• Crypto hype often distorts true market value.\n• Prices may become overinflated without fundamental support.\n• Caution is required to avoid entering bubbles.",
                      "• Evaluate both market sentiment and fundamental data together.\n• Use technical analysis to validate hype signals.\n• Cross-check with reliable sources for confirmation.",
                      "• Wait for market corrections before trading.\n• Reassess asset value when fundamentals stabilize.\n• Avoid impulsive decisions based solely on hype.",
                      "• Historical patterns show that hype-driven surges are frequently followed by sharp corrections."
                  ],
                  "mc-onbaordingquestion-61": [
                      "• Order flow analysis reveals real-time liquidity and market pressure.\n• It uncovers potential reversal signals early.\n• It enhances timing for trade entries.",
                      "• Use high-frequency data to monitor bid/ask dynamics.\n• Integrate order flow with technical indicators.\n• Execute trades using automated tools for precision.",
                      "• Adjust trade entries based on order flow insights.\n• Set dynamic stop-loss orders.\n• Continuously monitor market liquidity.",
                      "• Professional traders use order flow analysis to gain a competitive edge."
                  ],
                  "mtf-onbaordingquestion-10": [
                      "• Chart patterns help forecast future price movements.\n• They are derived from historical market behavior.\n• Patterns indicate potential reversals or continuations.",
                      "• Use robust charting software to identify key patterns in real time.\n• Confirm patterns with volume and trend data.\n• Compare with historical trends for validation.",
                      "• Identify key reversal signals and set alerts for breakouts.\n• Adjust your trading strategy based on pattern confirmation.\n• Use complementary technical indicators.",
                      "• Mastering chart patterns significantly improves technical analysis accuracy."
                  ],
                  "tf-onbaordingquestion-2": [
                      "• Stop-loss orders limit losses but cannot eliminate them.\n• They operate within the constraints of market volatility.\n• They are an important risk management tool.",
                      "• Use stop-loss orders along with other risk strategies.\n• Monitor trades to adjust stops dynamically.\n• Understand execution limitations in fast markets.",
                      "• Set realistic stop-loss levels based on current market conditions.\n• Regularly review and adjust them.\n• Combine stop-losses with proper position sizing.",
                      "• Stop-loss orders help manage risk, though market gaps can affect their performance."
                  ],
                  "tf-onbaordingquestion-3": [
                      "• Dollar cost averaging (DCA) involves investing fixed amounts at regular intervals.\n• It mitigates the risk of market timing.\n• It averages the purchase price over time.",
                      "• Set up automatic investment plans to implement DCA.\n• Use recurring purchase strategies with financial apps.\n• Monitor long-term performance to adjust investments.",
                      "• Invest consistently regardless of market conditions.\n• Track performance over time.\n• Adjust investment amounts as needed.",
                      "• DCA is widely used to steadily build wealth, reducing the impact of volatility."
                  ],
                  "mc-onbaordingquestion-62": [
                      "• Risk limits are essential for protecting trading capital.\n• They prevent excessive losses during downturns.\n• They enforce disciplined trading practices.",
                      "• Use predefined risk limits to exit positions early.\n• Monitor risk levels in real time.\n• Adjust thresholds as market conditions evolve.",
                      "• Implement strict stop-loss orders.\n• Regularly review risk-to-reward ratios.\n• Exit positions immediately if limits are breached.",
                      "• Top prop traders rely on rigorous risk management to safeguard capital over the long term."
                  ]
  };

  useEffect(() => {
    if (showAIChat && messages.length > 0) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleOptionSelect = (index) => {
    if (!showFeedback) {
      setSelectedOption(index);
      const correct = index.toString() === question.correctanswer;
      setIsCorrect(correct);
      setShowFeedback(true);
    }
  };

  const handleLearnWithAI = () => {
    setShowAIChat(true);
    setMessages([
      { sender: 'ai', text: `Learn with AI: Ask me anything about ${question.questionname}` }
    ]);
  };

  const handleSendMessage = () => {
    if (userMessage.trim() === '') return;

    setMessages((prevMessages) => [
      ...prevMessages,
      { sender: 'user', text: userMessage }
    ]);
    setUserMessage('');
    setUserMessageCount((count) => count + 1);

    setTimeout(() => {
      const responseList = aiResponses[question.questionid] || [];
      const responseIndex = userMessageCount < responseList.length ? userMessageCount : responseList.length - 1;
      const responseText = responseList.length > 0 ? responseList[responseIndex] : "That's an interesting question! Let's explore more together.";
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: 'ai', text: responseText }
      ]);
    }, 1500);
  };

  return (
    <StyledContainer>
      <StyledHeading>Multiple Choice Question</StyledHeading>
      <QuestionText>{question.questionname}</QuestionText>

      <Row className="g-3" style={{ marginTop: '20px' }}>
        {[1, 2, 3, 4].map((index) => {
          const option = question[`answeroption${index}`];
          if (!option) return null;

          return (
            <Col xs={12} key={index}>
              <StyledCard
                onClick={() => handleOptionSelect(index)}
                selected={selectedOption === index}
                isCorrect={isCorrect}
              >
                {option}
              </StyledCard>
            </Col>
          );
        })}
      </Row>

      {showFeedback && (
        <>
          {!isCorrect && <AIButton onClick={handleLearnWithAI}>Learn with AI</AIButton>}
          <Button
            onClick={onNext}
            style={{
              backgroundColor: '#FF7C24',
              width: '100%',
              marginTop: '10px',
              fontSize: '18px',
              fontWeight: 'bold',
              borderRadius: '10px',
              padding: '12px 30px',
            }}
          >
            Continue
          </Button>
        </>
      )}

      {/* AI Chat Modal */}
      <Modal show={showAIChat} onHide={() => setShowAIChat(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>AI Tutor</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                style={{
                  alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                  backgroundColor: msg.sender === 'user' ? '#007bff' : '#232830',
                  color: '#ffffff',
                  padding: '10px',
                  borderRadius: '10px',
                  maxWidth: '70%',
                  textAlign: msg.sender === 'user' ? 'right' : 'left'
                }}
              >
                {msg.text}
              </div>
            ))}
            <div ref={chatEndRef}></div>
          </div>
        </Modal.Body>
        <Modal.Footer 
  style={{ 
    display: 'flex', 
    flexDirection: 'column', 
    gap: '10px', 
    position: 'sticky', 
    bottom: 0, 
    backgroundColor: '#232830', 
    padding: '15px', 
    width: '100%' 
  }}
>
  {userMessageCount < 3 ? (
    <div style={{ display: 'flex', gap: '10px', width: '100%', alignItems: 'center', flexWrap: 'wrap' }}>
      <Form.Control
        type="text"
        placeholder="Ask me anything..."
        value={userMessage}
        onChange={(e) => setUserMessage(e.target.value)}
        style={{ flex: 1, minHeight: '50px', backgroundColor: '#fff', color: '#232830', borderRadius: '8px' }} 
      />
    <SendButton 
  onClick={handleSendMessage} 
  disabled={!userMessage.trim()} 
  style={{ 
    minWidth: '80px', 
    minHeight: '50px', 
    zIndex: 100, 
    flexShrink: 0, 
    backgroundColor: '#FF7C24', 
    borderColor: '#FF7C24', 
    color: '#fff' 
  }} 
>
  Send
</SendButton>
    </div>
  ) : (
    <UpgradeContainer>
      <p>Upgrade to FinTorro Pro and get:</p>
      <p>• Unlimited Lessons  </p>
      <p>• Unlimited Courses  </p>
      <p>• Unlimited Practice Sessions  </p>
      <p>• Unlimited Mistakes  </p>
      <p>• Learn with AI  </p>
      <p>• 10,000 Lessons </p> 
      <p>• Join Challenges  </p>
      <p>• Includes Crypto, Forex, Psychology</p>
      <UpgradeButton onClick={handleUpgradeClick}> Upgrade to Pro
</UpgradeButton>
    </UpgradeContainer>
  )}
</Modal.Footer>

      </Modal>
    </StyledContainer>
  );
};

export default OnboardingMultipleChoiceQuestion;


